import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { WorkspaceLink } from "../../hooks/useWsNavigate";

// Task 5 / Phase 6: the load-test summary shown once a stress test completes.
// Reads the `report` payload from `StressTests::Report#summary` — a pure
// read-side rollup, nothing here mutates anything.

const formatPercent = (rate) => {
  if (rate == null || Number.isNaN(rate)) return "—";
  return `${Math.round(rate * 100)}%`;
};

const formatMs = (ms) => (ms == null ? "—" : `${Math.round(ms)}ms`);

// `degradation_curve` (Phase 6) is `{ method, points, knee_concurrency, note }`
// — a true concurrency-vs-latency curve. Normalized defensively so an older
// cached payload (bare array, or dispatch-order points) still renders.
const getCurvePoints = (curve) => {
  if (!curve) return [];
  if (Array.isArray(curve)) return curve;
  if (Array.isArray(curve.points)) return curve.points;
  return [];
};

const getCurveNote = (curve) => {
  if (!curve || Array.isArray(curve)) return null;
  return curve.note || null;
};

const ATTRIBUTION_LABELS = {
  our_infra: "Our Infra",
  vendor: "Vendor",
  customer_bot: "Customer Bot",
  carrier: "Carrier",
};

function HeadlineCard({ label, value, sub }) {
  return (
    <div className="bg-card border border-border rounded-lg px-4 py-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold text-foreground mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export function LoadSummary({ report, batchId }) {
  if (!report) return null;

  const {
    connection_success_rate: successRate,
    latency_p90_ms: p90,
    achieved_concurrency: achieved,
    target_concurrency: target,
    target_not_met: targetNotMet,
    limiting_factor: limitingFactor,
    degradation_curve: degradationCurve,
    failure_attribution: failureAttribution,
    answer_latency: answerLatency,
    response_latency: responseLatency,
    code_metrics: codeMetrics,
    client_failures: clientFailures,
    delivery_success_rate: deliveryRate,
  } = report;

  const points = getCurvePoints(degradationCurve);
  const note = getCurveNote(degradationCurve);
  const kneeConcurrency =
    degradationCurve && !Array.isArray(degradationCurve)
      ? degradationCurve.knee_concurrency ?? null
      : null;

  // Prefer the true concurrency axis; fall back to the old dispatch-order
  // field so a stale cached payload still charts.
  const chartData = points.map((p) => ({
    x: p.concurrency ?? p.dispatch_order ?? p.run_id,
    latency: p.latency_p95_ms,
  }));

  const attributionBuckets = failureAttribution?.buckets || null;
  const totalFailed = failureAttribution?.total_failed ?? 0;

  const clientDidNotConnect = clientFailures?.did_not_connect ?? 0;
  const clientNotAnswered = clientFailures?.not_answered ?? 0;
  const totalClientFailures = clientFailures?.total ?? 0;

  const answerP95 = answerLatency?.p95_ms;
  const responseP95 = responseLatency?.p95_ms;
  const connectedRate = codeMetrics?.connected?.rate;
  const hadAudioRate = codeMetrics?.had_audio?.rate;

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <HeadlineCard
          label="Delivery Success"
          value={formatPercent(deliveryRate)}
          sub="our side — calls we placed"
        />
        <HeadlineCard label="Answered" value={formatPercent(successRate)} sub="reached the bot" />
        <HeadlineCard label="P90 Latency" value={formatMs(p90)} />
        <HeadlineCard
          label="Concurrency"
          value={`${achieved ?? "—"} / ${target ?? "—"}`}
          sub="achieved / target"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <HeadlineCard label="Answer Latency (P95)" value={formatMs(answerP95)} sub="connect → first reply" />
        <HeadlineCard label="Response Latency (P95)" value={formatMs(responseP95)} sub="ongoing turns" />
        <HeadlineCard label="Connected" value={formatPercent(connectedRate)} sub="calls answered" />
        <HeadlineCard label="Had Audio" value={formatPercent(hadAudioRate)} sub="real conversation" />
      </div>

      {targetNotMet && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm text-destructive">
            <span className="font-medium">Target concurrency not met.</span>{" "}
            {limitingFactor || "No limiting factor was reported."}
          </div>
        </div>
      )}

      {totalClientFailures > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Client-Side Failures
            <span className="ml-1 text-muted-foreground/70">
              ({totalClientFailures} — the target bot's side, not ours)
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md bg-muted px-3 py-2">
              <div className="text-xs text-muted-foreground">Did not connect</div>
              <div className="text-lg font-semibold text-foreground mt-0.5">{clientDidNotConnect}</div>
              <div className="text-[11px] text-muted-foreground/70">never reached the bot</div>
            </div>
            <div className="rounded-md bg-muted px-3 py-2">
              <div className="text-xs text-muted-foreground">Not answered</div>
              <div className="text-lg font-semibold text-foreground mt-0.5">{clientNotAnswered}</div>
              <div className="text-[11px] text-muted-foreground/70">rang, no pickup</div>
            </div>
          </div>
        </div>
      )}

      {attributionBuckets && totalFailed > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Failure Attribution
            <span className="ml-1 text-muted-foreground/70">
              ({totalFailed} failed {totalFailed === 1 ? "call" : "calls"})
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.keys(ATTRIBUTION_LABELS).map((key) => (
              <div key={key} className="rounded-md bg-muted px-3 py-2">
                <div className="text-xs text-muted-foreground">{ATTRIBUTION_LABELS[key]}</div>
                <div className="text-lg font-semibold text-foreground mt-0.5">
                  {attributionBuckets[key] ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Latency vs. Concurrency
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="x"
                type="number"
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 11 }}
                label={{ value: "concurrent calls", position: "insideBottom", offset: -2, fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} unit="ms" />
              <Tooltip
                formatter={(v) => [`${v}ms`, "P95 Latency"]}
                labelFormatter={(x) => `${x} concurrent`}
              />
              {kneeConcurrency != null && (
                <ReferenceLine
                  x={kneeConcurrency}
                  stroke="#ef4444"
                  strokeDasharray="4 4"
                  label={{ value: "knee", position: "top", fontSize: 11, fill: "#ef4444" }}
                />
              )}
              <Line
                type="monotone"
                dataKey="latency"
                stroke="#6366f1"
                strokeWidth={2}
                dot={{ r: 3, fill: "#6366f1" }}
              />
            </LineChart>
          </ResponsiveContainer>
          {note && <p className="text-xs text-muted-foreground mt-2">{note}</p>}
        </div>
      )}

      {batchId && (
        <WorkspaceLink
          to={`/batch-run/${batchId}`}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          View call details
          <ArrowRight className="h-3.5 w-3.5" />
        </WorkspaceLink>
      )}
    </div>
  );
}

export default LoadSummary;
